import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, RotateCcw, Clock, FileText, Eye } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';
import { formatDate } from '@/lib/taxonomies';

/**
 * VersionHistorySheet
 * Right-side sheet that lists the last 20 snapshots of a Season entry or
 * Field Note. Owner can preview any snapshot's body inline, and Restore it
 * (which itself snapshots the current version first, so a restore is undoable).
 *
 * Props:
 *   - entryType: 'season' | 'note'
 *   - entryId: string | null (null disables)
 *   - open, onOpenChange
 *   - onRestored(entry): called with the restored entry payload; parent should
 *     reload its form from this data.
 */
export const VersionHistorySheet = ({ entryType, entryId, open, onOpenChange, onRestored }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null); // full revision doc
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    if (!entryId) return;
    setLoading(true);
    try {
      const rows = await adminApi.listRevisions(entryType, entryId);
      setItems(rows);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load version history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && entryId) load();
    if (!open) {
      setSelectedPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryId, entryType]);

  const preview = async (id) => {
    try {
      const full = await adminApi.getRevision(id);
      setSelectedPreview(full);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed.');
    }
  };

  const restore = async (id) => {
    setRestoring(true);
    try {
      const res = await adminApi.restoreRevision(id);
      toast.success('Restored. Editor updated.');
      onRestored?.(res.entry);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed.');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[520px] flex flex-col" data-testid="version-history-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History size={18} /> Version history
          </SheetTitle>
          <SheetDescription>
            The most recent {items.length || 20} snapshots (a new one is kept every time you save or autosave). Restoring a
            version also snapshots the current text, so you can always come back.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex-1 overflow-y-auto pr-1 space-y-3">
          {loading && <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading history…</p>}
          {!loading && items.length === 0 && (
            <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-6 text-center">
              <p className="font-serif text-lg">No versions yet.</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                Snapshots start being saved the next time this post is updated.
              </p>
            </div>
          )}
          {!loading && items.map((r) => (
            <div
              key={r.id}
              data-testid="version-row"
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.title || 'Untitled'}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
                    <Clock size={11} /> {formatDate(r.created_at)}
                    {r.status && (
                      <Badge variant={r.status === 'published' ? 'default' : 'secondary'} className="ml-2 h-4 px-1.5 text-[10px]">
                        {r.status}
                      </Badge>
                    )}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => preview(r.id)}
                    data-testid="version-preview"
                    aria-label="Preview this version"
                  >
                    <Eye size={14} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        data-testid="version-restore"
                        aria-label="Restore this version"
                      >
                        <RotateCcw size={14} className="mr-1" /> Restore
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Restore this version?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your current text will be snapshotted first, so you can restore it back later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => restore(r.id)} disabled={restoring} data-testid="version-restore-confirm">
                          {restoring ? 'Restoring…' : 'Restore'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              {r.summary && (
                <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{r.summary}</p>
              )}
              {r.body_preview && (
                <p className="mt-1 text-xs text-[hsl(var(--foreground))]/85 line-clamp-3">
                  <FileText size={11} className="inline mr-1 mb-0.5" /> {r.body_preview}…
                </p>
              )}
            </div>
          ))}

          {selectedPreview && (
            <div
              className="mt-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary)/0.4)] p-4"
              data-testid="version-preview-panel"
            >
              <p className="kicker">Preview</p>
              <h3 className="font-serif text-lg mt-1">
                {selectedPreview.snapshot?.title || 'Untitled'}
              </h3>
              {selectedPreview.snapshot?.summary && (
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {selectedPreview.snapshot.summary}
                </p>
              )}
              {selectedPreview.snapshot?.excerpt && !selectedPreview.snapshot?.summary && (
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {selectedPreview.snapshot.excerpt}
                </p>
              )}
              <pre className="mt-3 whitespace-pre-wrap text-xs text-[hsl(var(--foreground))]/85 max-h-72 overflow-y-auto">
                {selectedPreview.snapshot?.body || '(no body text)'}
              </pre>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => setSelectedPreview(null)}>
                  Close preview
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
